import { saveAs } from "file-saver";
import api from "../api";
import {
  IRecorteDescarga,
  leerMensajeLimiteDescarga,
  leerRecorteDescarga,
} from "../../utils/limiteDescarga";

/**
 * Descarga del "Excel Master" de Rex.
 *
 * El archivo lo arma el backend (POST excel-master-rex): acá solo se envía la
 * lista de ruts seleccionados y se guarda el binario que llega. Antes esto se
 * generaba en el navegador pidiendo la ficha de cada candidato por separado,
 * lo que disparaba una request HTTP por persona.
 */

export interface IResultadoExcelMaster {
  exportados: number;
  /** Ruts que el backend no pudo incluir (sin ficha o fuera del tope). */
  sinDatos: string[];
  nombreArchivo: string;
  /** Sin selección: se exportaron solo los primeros MAX_REGISTROS_DESCARGA. */
  recorte: IRecorteDescarga;
}

const NOMBRE_POR_DEFECTO = "EXCEL_MASTER_REX.xlsx";

/** Lee el nombre del archivo desde los headers, como hace postGenerarExcel. */
const obtenerNombreArchivo = (headers: any): string => {
  const disposition: string =
    headers?.["content-disposition"] ?? headers?.["Content-Disposition"] ?? "";
  const desdeHeader: string =
    headers?.["x-filename"] ?? headers?.["X-Filename"] ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  return desdeHeader || (match && match[1]) || NOMBRE_POR_DEFECTO;
};

const parsearSinDatos = (headers: any): string[] => {
  const crudo: string =
    headers?.["x-no-processed"] ?? headers?.["X-No-Processed"] ?? "";
  if (!crudo) return [];
  try {
    const lista = JSON.parse(crudo);
    return Array.isArray(lista) ? lista : [];
  } catch (error) {
    return [];
  }
};

/**
 * Cuando la respuesta es un error, axios igual la entrega como Blob por el
 * responseType. Se lee para poder mostrar el mensaje real del backend.
 */
const leerMensajeDeError = async (error: any): Promise<string | undefined> => {
  const data = error?.response?.data;
  if (!data) return undefined;
  try {
    const texto: string =
      typeof data?.text === "function" ? await data.text() : String(data);
    return JSON.parse(texto)?.message;
  } catch (parseError) {
    return undefined;
  }
};

/** Nombre del error cuando la descarga superó el tope de registros. */
export const ERROR_LIMITE_DESCARGA = "ErrorLimiteDescarga";

export interface IParamsExcelMaster {
  /** Ruts seleccionados. Si va vacío, el backend exporta todo el filtro. */
  rutList: string[];
  /** Estados de la pestaña, igual que los de "Descargar Nómina". */
  estados: any;
  /** Filtros vigentes de la tabla. */
  filtros: any;
}

export const descargarExcelMasterRex = async (
  params: IParamsExcelMaster
): Promise<IResultadoExcelMaster> => {
  const { rutList, estados, filtros } = params;
  try {
    const result = await api.post(
      `excel-master-rex`,
      { rutList, estados, filtros },
      {
        params: { userId: sessionStorage.getItem("usuarioId") as string },
        responseType: "blob",
      }
    );

    const nombreArchivo: string = obtenerNombreArchivo(result.headers);
    const sinDatos: string[] = parsearSinDatos(result.headers);
    const exportados: number = Number(
      result.headers?.["x-total-processed"] ??
        result.headers?.["X-Total-Processed"] ??
        0
    );

    saveAs(result.data, nombreArchivo);

    return {
      exportados,
      sinDatos,
      nombreArchivo,
      recorte: leerRecorteDescarga(result.headers),
    };
  } catch (error: any) {
    const mensajeLimite: string | undefined = await leerMensajeLimiteDescarga(error);
    if (mensajeLimite) {
      const errorLimite = new Error(mensajeLimite);
      errorLimite.name = ERROR_LIMITE_DESCARGA;
      throw errorLimite;
    }
    const mensaje: string | undefined = await leerMensajeDeError(error);
    throw new Error(mensaje ?? "No se pudo generar el Excel Master");
  }
};
